import "./styles.css";

type Meld = number[];
type MechanicalTier = "practical" | "advanced";
type StrategyMode = "single-game" | "balanced-match" | "lead-protection" | "comeback-variance";
type DrawerTab = "coach" | "players" | "activity" | "replay" | "configuration";
type RackSortMode = "color" | "number";
type BoardViewMode = "fit" | "large";
type OpponentSpeed = "instant" | "quick" | "natural" | "thoughtful";
type BoardSortMode = "natural" | "number" | "runs-first" | "groups-first";

interface Opponent {
  seat: number;
  policy: string;
  rack_count: number;
  opened: boolean;
  mechanical_tier: MechanicalTier;
  strategy_mode: StrategyMode;
}

interface BotMove {
  player: number;
  before: Meld[];
  after: Meld[];
  explanation: string;
  action: Record<string, unknown>;
}

interface OpponentPlayback {
  moves: BotMove[];
  index: number;
  phase: "thinking" | "result";
  finalMessage: string;
  manual: boolean;
}

interface ReplaySnapshot {
  step: number;
  label: string;
  player: number | null;
  table: Meld[];
  rack_counts: number[];
  pool_size: number;
}

interface ReplayView {
  verified: boolean;
  snapshots: ReplaySnapshot[];
  outcome: { kind: string; winners: number[]; scores: number[] };
}

interface MatchView {
  profile: { type: "family_score"; games: number } | { type: "official"; rounds: number; players: number };
  completed_games: number;
  games_remaining: number;
  cumulative_scores: number[];
  game_wins: number[];
  next_starting_player: number;
  complete: boolean;
  winners: number[];
}

interface GameView {
  schema_version: number;
  session_id: string;
  revision: number;
  human_seat: number;
  human_turn: boolean;
  current_player: number;
  turn_index: number;
  rack: number[];
  opened: boolean;
  human_tier: MechanicalTier;
  table: Meld[];
  pool_size: number;
  opponents: Opponent[];
  outcome: { kind: string; winners: number[]; scores: number[] } | null;
  bot_moves: BotMove[];
  history: { turn_index: number; player: number; type: "played" | "drew" | "passed" }[];
  match: MatchView | null;
}

type Source = { kind: "rack" } | { kind: "meld"; index: number };
interface Selection { tile: number; source: Source }
interface Snapshot { rack: number[]; table: Meld[] }
interface Validation { state: "idle" | "checking" | "legal" | "illegal"; message: string }
interface ViewState {
  tableTop: number;
  tableLeft: number;
  rackLeft: number;
  rackTop: number;
  drawerTop: number;
  focus: { id?: string; tile?: number; source?: string } | null;
}

const strategyGroups = [
  { label: "Strong general play", values: ["balanced", "greedy_tiles", "greedy_points"] },
  { label: "Risk and timing", values: ["conservative", "strategic_drawer", "late_opening_hoarder", "endgame_liquidator"] },
  { label: "Tile-shape specialists", values: ["joker_holder", "joker_farmer", "pivot_copy_keeper", "table_denial"] },
  { label: "Human-style constraints", values: ["manipulation_averse", "eager_beginner"] },
  { label: "Adaptive and search", values: ["opponent_modeler", "shallow_planner", "match_aware", "random_legal"] },
];

const opponentSpeedOptions: Array<{ value: OpponentSpeed; label: string; timing: string; description: string }> = [
  { value: "instant", label: "Instant", timing: "No pause", description: "Skip the presentation and return to your turn immediately." },
  { value: "quick", label: "Quick Draw", timing: "About 1–2 seconds", description: "Keep the visual explanation, but move briskly around the table." },
  { value: "natural", label: "Table Pace", timing: "About 2–4 seconds", description: "A comfortable pace for following each opponent’s decision." },
  { value: "thoughtful", label: "Take Your Time", timing: "About 4–6 seconds", description: "Leave extra time to inspect every table change before continuing." },
];

const boardSortOptions: Array<{ value: BoardSortMode; label: string; description: string }> = [
  { value: "natural", label: "Natural table", description: "Keep melds where they were played." },
  { value: "number", label: "Number order", description: "Arrange from low to high." },
  { value: "runs-first", label: "Runs first", description: "Runs by color, then groups." },
  { value: "groups-first", label: "Groups first", description: "Groups by number, then runs." },
];

const appElement = document.querySelector<HTMLDivElement>("#app");
if (!appElement) throw new Error("Missing #app");
const app = appElement;

let game: GameView | null = null;
let workingRack: number[] = [];
let workingTable: Meld[] = [];
let undoStack: Snapshot[] = [];
let redoStack: Snapshot[] = [];
let selections: Selection[] = [];
let message = "Choose your opponents and deal a game.";
let hint: { action?: { type: string; proposed_table?: Meld[]; rack_tiles?: number[] } | null; explanation?: string; search?: { status?: string; exhaustive?: boolean; winning?: boolean } } | null = null;
let hintRequestTier: "practical" | "advanced" | "oracle" | null = null;
let botStep = 0;
let replay: ReplayView | null = null;
let replayStep = 0;
let drawer: DrawerTab | null = null;
let drawerOpening = false;
let opponentPlayback: OpponentPlayback | null = null;
let recentDrawnTile: number | null = null;
let recentDrawIndicatorFresh = false;
let outcomeDismissed = false;
let validation: Validation = { state: "idle", message: "Move tiles to begin." };
let validationHandle: number | null = null;
let playbackHandle: number | null = null;
let requestInFlight = false;
let timerSeconds = Number(localStorage.getItem("rummikub-turn-timer") || "0");
let rackSortMode: RackSortMode = localStorage.getItem("rummikub-rack-sort") === "number" ? "number" : "color";
let opponentSpeed: OpponentSpeed = readOpponentSpeed();
let boardSortMode: BoardSortMode = readBoardSortMode();
let boardViewMode: BoardViewMode = "fit";
let timerDeadline = 0;
let timerHandle: number | null = null;
let resizeHandle: number | null = null;

function render(): void {
  const view = captureViewState();
  document.body.classList.toggle("game-active", Boolean(game));
  app.innerHTML = game ? renderGame() : renderNewGame();
  recentDrawIndicatorFresh = false;
  drawerOpening = false;
  bindEvents();
  updateTimerDisplay();
  fitTableToViewport();
  fitRackToViewport();
  restoreViewState(view);
}

function captureViewState(): ViewState {
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const table = document.querySelector<HTMLElement>(".table-surface");
  const rack = document.querySelector<HTMLElement>(".rack-tiles");
  const drawerBody = document.querySelector<HTMLElement>(".drawer-body");
  return {
    tableTop: table?.scrollTop || 0,
    tableLeft: table?.scrollLeft || 0,
    rackLeft: rack?.scrollLeft || 0,
    rackTop: rack?.scrollTop || 0,
    drawerTop: drawerBody?.scrollTop || 0,
    focus: active?.id
      ? { id: active.id }
      : active?.classList.contains("tile")
        ? { tile: Number(active.dataset.tile), source: active.dataset.source }
        : null,
  };
}

function restoreViewState(view: ViewState): void {
  let focusTarget: HTMLElement | null = view.focus?.id
    ? document.getElementById(view.focus.id)
    : null;
  if (!focusTarget && view.focus?.tile !== undefined) {
    focusTarget = [...document.querySelectorAll<HTMLElement>(".tile")].find((tile) =>
      Number(tile.dataset.tile) === view.focus?.tile && tile.dataset.source === view.focus?.source
    ) || null;
  }
  focusTarget?.focus({ preventScroll: true });
  const table = document.querySelector<HTMLElement>(".table-surface");
  const rack = document.querySelector<HTMLElement>(".rack-tiles");
  const drawerBody = document.querySelector<HTMLElement>(".drawer-body");
  if (table) {
    table.scrollTop = view.tableTop;
    table.scrollLeft = view.tableLeft;
  }
  if (rack) {
    rack.scrollLeft = view.rackLeft;
    rack.scrollTop = view.rackTop;
  }
  if (drawerBody) drawerBody.scrollTop = view.drawerTop;
}

function renderNewGame(): string {
  return `
    <main class="setup-shell">
      <section class="setup-intro">
        <a class="brand-link" href="${sitePath("/games/rummikub")}"><span class="brand-mark">R</span><span>gvaughan.com</span></a>
        <p class="eyebrow">Strategy research you can play</p>
        <h1>Rummikub<br><em>Strategy Lab</em></h1>
        <p class="setup-lede">Practice real decisions against explainable opponents. Every move is checked by the same rules engine used in the research simulator.</p>
        <div class="setup-promises" aria-label="Game features">
          <span>✓ Official tile set and meld rules</span>
          <span>✓ Practical, human-achievable strategies</span>
          <span>✓ Hints that explain why—not just what</span>
        </div>
      </section>
      <section class="setup-panel" aria-labelledby="setup-title">
        <div class="setup-panel-heading"><div><p class="step-label">Game setup</p><h2 id="setup-title">Choose your table</h2></div><span class="setup-time">About 15 min</span></div>
        <div class="setup-grid">
          <label>Players<select id="player-count">
            <option value="2">2 players</option><option value="3">3 players</option><option value="4" selected>4 players</option>
          </select></label>
          <label>Format<select id="match-profile">
            <option value="single_game">Single game</option>
            <option value="family_score_5">Family match · 5 games</option>
            <option value="family_score_6">Family match · 6 games</option>
            <option value="official_four_player">Official round · wins</option>
          </select></label>
        </div>
        <fieldset class="opponent-fieldset"><legend>Opponents</legend>
          <div class="opponent-help-row"><p class="field-help">Each style is a testable strategy, not a difficulty disguise.</p><button id="randomize-opponents" type="button" class="randomize-button" title="Choose different strategy styles"><span aria-hidden="true">⚄</span> Randomize</button></div>
          <div class="opponent-selects">
            ${[1, 2, 3].map((seat) => renderOpponentChoice(seat)).join("")}
          </div>
        </fieldset>
        <details class="advanced-setup"><summary>Game options</summary><div class="setup-grid advanced-grid">
          <label>Seed <span class="label-help">Optional · enter one to replay an exact deal</span><input id="seed" type="number" min="0" step="1" inputmode="numeric" placeholder="Random each deal"></label>
          <label>Turn reminder<select id="turn-timer">
            <option value="0" ${timerSeconds === 0 ? "selected" : ""}>Off</option>
            <option value="60" ${timerSeconds === 60 ? "selected" : ""}>60 seconds</option>
            <option value="90" ${timerSeconds === 90 ? "selected" : ""}>90 seconds</option>
          </select></label>
        </div></details>
        <button id="new-game" class="primary deal-button"><span>Deal tiles</span><kbd>↵</kbd></button>
        <p class="setup-note">You’ll play as the starting player. No account or saved personal data.</p>
        <p class="sr-only" role="status">${escapeHtml(message)}</p>
      </section>
    </main>`;
}

function renderOpponentChoice(seat: number): string {
  const defaults = ["balanced", "greedy_tiles", "conservative"];
  return `<div class="opponent-choice" data-opponent-seat="${seat}">
    <span class="seat-token">${seat + 1}</span>
    <label>Opponent ${seat}<select id="opponent-${seat}">${strategyGroups.map((group) => `<optgroup label="${group.label}">${group.values.map((policy) => `<option value="${policy}" ${policy === defaults[seat - 1] ? "selected" : ""}>${displayName(policy)}</option>`).join("")}</optgroup>`).join("")}</select></label>
    <label class="tier-label"><span class="sr-only">Opponent ${seat} tier</span><select id="tier-${seat}" aria-label="Opponent ${seat} tier"><option value="practical">Practical</option><option value="advanced">Advanced</option></select></label>
  </div>`;
}

function renderGame(): string {
  if (!game) return "";
  const selectedCount = selections.length;
  const dirty = workspaceDirty();
  const playbackMove = currentPlaybackMove();
  const humanIsActive = game.human_turn && !playbackMove;
  const canAct = humanIsActive && !game.outcome && !requestInFlight;
  const rackRows = splitRackRows(workingRack);
  return `
    <main class="game-shell ${drawer ? "drawer-open" : ""}">
      <header class="game-topbar">
        <div class="game-identity"><button id="leave-game" class="icon-button" aria-label="Leave game" title="Leave game">‹</button><a href="${sitePath("/games/rummikub")}" class="compact-brand"><span class="brand-mark">R</span><span>Strategy Lab</span></a></div>
        <div class="turn-callout ${humanIsActive ? "is-human" : ""}"><span class="turn-pulse"></span><strong>${playbackMove ? `Player ${playbackMove.player + 1} is playing` : game.human_turn ? "Your turn" : `Player ${game.current_player + 1} is playing`}</strong><span>Turn ${game.turn_index + 1}</span></div>
        <div class="game-vitals" aria-label="Game status">
          <span><i class="pool-icon" aria-hidden="true"></i><b>${game.pool_size}</b><small>pool</small></span>
          <span><b>${workingRack.length}</b><small>rack</small></span>
          ${timerSeconds ? `<span class="timer-vital"><b id="turn-timer-display">${timerSeconds}s</b><small>timer</small></span>` : `<span id="turn-timer-display" hidden>Off</span>`}
          <button class="topbar-button ${drawer === "coach" ? "active" : ""}" data-open-drawer="coach">Coach</button>
          <button class="icon-button more-button ${drawer && drawer !== "coach" ? "active" : ""}" data-open-drawer="activity" aria-label="Open game details" title="Game details">•••</button>
        </div>
      </header>

      <section class="opponent-rail" aria-label="Opponents">
        ${game.opponents.map((opponent) => renderOpponent(opponent, playbackMove?.player === opponent.seat)).join("")}
        ${renderCompactMatch(game.match)}
      </section>

      <section class="table-surface" aria-labelledby="table-title" data-board-view="${boardViewMode}">
        <div class="table-toolbar">
          <div class="table-heading"><p class="surface-label">Shared table</p><h1 id="table-title">${workingTable.length ? `${workingTable.filter((meld) => meld.length).length} meld${workingTable.filter((meld) => meld.length).length === 1 ? "" : "s"}` : "Build your opening move"}</h1></div>
          <div class="playback-slot">
            ${opponentPlayback ? renderOpponentPlayback(opponentPlayback) : `<div class="table-turn-note"><span aria-hidden="true">${humanIsActive ? "◆" : "○"}</span><div><strong>${humanIsActive ? "Your move" : "Table ready"}</strong><small>${humanIsActive ? "Build a meld below or draw from the pool." : "Waiting for the next action."}</small></div></div>`}
          </div>
          <div class="workspace-actions" aria-label="Workspace actions">
            <button id="board-view" class="quiet-button board-view-button" aria-label="Board view: ${boardViewMode === "fit" ? "fit all tiles" : "large tiles"}; switch to ${boardViewMode === "fit" ? "large tiles" : "fit all tiles"}" title="${boardViewMode === "fit" ? "Use larger tiles and allow table scrolling" : "Fit the complete table in view"}">⊞ <span>${boardViewMode === "fit" ? "Fit" : "Large"}</span></button>
            <button id="undo" class="quiet-button" ${undoStack.length && canAct ? "" : "disabled"} title="Undo (Ctrl/Command Z)">↶ <span>Undo</span></button>
            <button id="redo" class="quiet-button" ${redoStack.length && canAct ? "" : "disabled"} title="Redo (Ctrl/Command Shift Z)">↷ <span>Redo</span></button>
            <button id="reset" class="quiet-button" ${dirty && canAct ? "" : "disabled"}>Reset</button>
          </div>
        </div>
        <div id="melds" class="melds" aria-label="Meld workspace">
          ${workingTable.map(renderMeld).join("")}
          <button id="new-meld" class="new-meld-zone drop-zone" data-target="new-meld" ${canAct ? "" : "disabled"}>
            <span class="plus">+</span><strong>${selectedCount ? `Make meld with ${selectedCount}` : "New meld"}</strong><small>${selectedCount ? "Place selected tiles together" : "Select tiles, then tap here"}</small>
          </button>
        </div>
        ${!workingTable.length && !selectedCount ? `<div class="table-empty-guide" aria-hidden="true"><span>1</span><p><strong>Select tiles on your rack</strong><br>Tap each tile you want in a set or run.</p><i></i><span>2</span><p><strong>Make a meld</strong><br>Use the “New meld” target above.</p></div>` : ""}
      </section>

      <section class="player-dock rack" aria-labelledby="rack-title">
        <div class="dock-status">
          <div class="opening-progress ${game.opened ? "complete" : ""}">
            <span class="opening-icon">${game.opened ? "✓" : "30"}</span>
            <div><strong>${game.opened ? "Opening complete" : "Opening: 30 points"}</strong><small>${game.opened ? "You may rearrange table melds" : openingSummary()}</small></div>
          </div>
          <div class="validation-chip ${validation.state}" aria-label="Move validation">
            <span></span><div><strong>${validationTitle()}</strong><small>${escapeHtml(validation.message)}</small></div>
          </div>
        </div>
        <div class="rack-heading">
          <div><p class="surface-label">Private</p><h2 id="rack-title">Your rack <span>${workingRack.length}</span></h2></div>
          ${recentDrawnTile === null ? "" : renderDrawnCallout(recentDrawnTile)}
          <div class="selection-tools ${selectedCount ? "visible" : ""}">
            <strong>${selectedCount} selected</strong>
            <button id="make-meld" ${selectedCount && canAct ? "" : "disabled"}>Make meld</button>
            <button id="return-selected" ${selectedCount && canReturnSelection() && canAct ? "" : "disabled"}>Return to rack</button>
            <button id="clear-selection" class="icon-button" ${selectedCount ? "" : "disabled"} aria-label="Clear selection">×</button>
          </div>
          <button id="sort-rack" class="quiet-button rack-sort" aria-label="Rack sorted by ${rackSortMode}; switch to ${rackSortMode === "color" ? "number" : "color"}" title="Switch to ${rackSortMode === "color" ? "number-first" : "color-first"} sorting" ${requestInFlight ? "disabled" : ""}><span>Sort</span><strong>${rackSortMode === "color" ? "Color" : "Number"}</strong><i aria-hidden="true">⇄</i></button>
        </div>
        <div class="rack-row">
          <div class="rack-tiles drop-zone" data-target="rack" aria-label="Your rack tiles">
            ${rackRows.map((row, index) => `<div class="rack-tier" data-rack-tier="${index + 1}">${row.map((tile) => renderTile(tile, { kind: "rack" })).join("")}</div>`).join("")}
          </div>
          <div class="turn-actions">
            <button id="draw" class="draw-button" ${canAct ? "" : "disabled"}><span class="draw-stack" aria-hidden="true"></span><span>${game.pool_size ? "Draw tile" : "Pass"}</span></button>
            ${game.outcome && outcomeDismissed ? renderPostGameAction() : `<button id="submit" class="primary submit-button" ${canAct && validation.state === "legal" ? "" : "disabled"}><span>Submit move</span><small>${validation.state === "checking" ? "Checking table…" : playedRackTiles().length ? `${playedRackTiles().length} tile${playedRackTiles().length === 1 ? "" : "s"} from rack` : "Play at least one tile"}</small></button>`}
          </div>
        </div>
        <div class="message-bar ${messageTone()}" role="status"><span>${messageIcon()}</span><p>${escapeHtml(message)}</p><button id="preview" ${canAct && dirty ? "" : "disabled"}>Check now</button></div>
      </section>

      ${drawer ? renderDrawer(drawer) : ""}
      ${game.outcome && !outcomeDismissed && !drawer && !opponentPlayback ? renderOutcome() : ""}
    </main>`;
}

function renderOpponent(opponent: Opponent, isPlaying = false): string {
  return `<button class="opponent ${isPlaying ? "is-playing" : ""}" data-open-drawer="players" aria-label="Player ${opponent.seat + 1}, ${opponent.rack_count} tiles, ${displayName(opponent.policy)}">
    <span class="opponent-avatar">P${opponent.seat + 1}</span><span class="opponent-copy"><strong>${displayName(opponent.policy)}</strong><small>${opponent.opened ? "Opened" : "Needs 30"} · ${displayName(opponent.strategy_mode)}</small></span>
    <span class="rack-count"><b>${opponent.rack_count}</b><small>tiles</small></span>
  </button>`;
}

function renderOpponentPlayback(playback: OpponentPlayback): string {
  const move = playback.moves[playback.index];
  const action = String(move.action.type || "move");
  const policy = game?.opponents.find((opponent) => opponent.seat === move.player)?.policy;
  const rackTiles = Array.isArray(move.action.rack_tiles) ? move.action.rack_tiles.length : 0;
  const result = action === "play"
    ? `Played ${rackTiles} tile${rackTiles === 1 ? "" : "s"}`
    : action === "draw" ? "Drew from the pool" : "Passed";
  const detail = playback.phase === "thinking"
    ? `${policy ? displayName(policy) : "Opponent"} is reading the table…`
    : move.explanation || result;
  const nextLabel = playback.phase === "thinking"
    ? "Show move"
    : playback.index === playback.moves.length - 1 ? "Continue" : "Next player";
  return `<div class="opponent-playback ${playback.phase} ${playback.manual ? "manual" : "auto"}" role="log" aria-label="Opponent turn playback" aria-live="polite">
    <span class="playback-avatar">P${move.player + 1}</span>
    <span class="playback-action" aria-hidden="true">${playback.phase === "thinking" ? "<i></i><i></i><i></i>" : action === "play" ? "↗" : action === "draw" ? "▯" : "—"}</span>
    <div class="playback-copy"><small>Opponent ${playback.index + 1} of ${playback.moves.length} · ${playback.manual ? "Step mode" : "Auto"}</small><strong>${playback.phase === "thinking" ? `Player ${move.player + 1} is deciding` : result}</strong><p>${escapeHtml(detail)}</p></div>
    <div class="playback-controls"><button id="next-playback" type="button">${nextLabel}</button><button id="skip-playback" type="button">Skip all</button></div>
  </div>`;
}

function renderDrawnCallout(tile: number): string {
  const info = tileInfo(tile);
  return `<div class="drawn-callout ${recentDrawIndicatorFresh ? "is-fresh" : ""}" aria-label="Just drew ${info.label}"><span>Just drew</span><strong><i class="${info.className}" aria-hidden="true"></i>${info.label}</strong></div>`;
}

function renderCompactMatch(match: MatchView | null): string {
  if (!match) return "";
  const target = match.profile.type === "family_score" ? match.profile.games : match.profile.rounds * match.profile.players;
  return `<button class="match-peek" data-open-drawer="players"><span>Game</span><strong>${Math.min(match.completed_games + 1, target)} / ${target}</strong><small>Standings ›</small></button>`;
}

function renderDrawer(tab: DrawerTab): string {
  return `<aside class="game-drawer ${drawerOpening ? "opening" : ""}" aria-label="Game details">
    <div class="drawer-header"><div><p class="eyebrow">At the table</p><h2>${drawerTitle(tab)}</h2></div><button id="close-drawer" class="icon-button" aria-label="Close game details">×</button></div>
    <nav class="drawer-tabs" aria-label="Game detail sections">
      ${drawerTab("coach", "Coach")}${drawerTab("players", "Players")}${drawerTab("activity", "Activity")}${replay || game?.outcome ? drawerTab("replay", "Replay") : ""}${drawerTab("configuration", "Settings")}
    </nav>
    <div class="drawer-body">${tab === "coach" ? renderCoach() : tab === "players" ? renderPlayers() : tab === "replay" ? renderReplay() : tab === "configuration" ? renderConfiguration() : renderActivity()}</div>
  </aside><button class="drawer-scrim" id="close-drawer-scrim" aria-label="Dismiss details overlay"></button>`;
}

function drawerTitle(tab: DrawerTab): string {
  if (tab === "coach") return "Strategy coach";
  if (tab === "players") return "Players & match";
  if (tab === "activity") return "Game activity";
  if (tab === "replay") return "Verified replay";
  return "Table configuration";
}

function drawerTab(value: DrawerTab, label: string): string {
  return `<button data-drawer-tab="${value}" class="${drawer === value ? "active" : ""}">${label}</button>`;
}

function renderConfiguration(): string {
  return `<section class="configuration-panel">
    <div class="configuration-intro"><span aria-hidden="true">◷</span><div><strong>Opponent move speed</strong><p>Choose how long the table pauses while showing each opponent’s decision. This only changes the presentation—not how quickly strategies calculate or how strongly they play.</p></div></div>
    <fieldset class="speed-options"><legend>Playback pace</legend>
      ${opponentSpeedOptions.map((option) => `<label class="speed-option ${opponentSpeed === option.value ? "selected" : ""}"><input type="radio" name="opponent-speed" value="${option.value}" ${opponentSpeed === option.value ? "checked" : ""}><span><strong>${option.label}${option.value === "natural" ? " <em>Default</em>" : ""}</strong><small>${option.description}</small></span><b>${option.timing}</b></label>`).join("")}
    </fieldset>
    <p class="configuration-note">You can still pause any animated turn by hovering over it, step forward manually, or choose “Skip all.”</p>
    <div class="configuration-divider" aria-hidden="true"></div>
    <div class="configuration-intro board-order-intro"><span aria-hidden="true">⇅</span><div><strong>Board organization</strong><p>Choose how complete melds are arranged between turns. Tiles inside each run or group remain in their natural order.</p></div></div>
    <fieldset class="board-order-options"><legend>Auto-sort melds</legend>
      ${boardSortOptions.map((option) => `<label class="board-order-option ${boardSortMode === option.value ? "selected" : ""}"><input type="radio" name="board-sort" value="${option.value}" ${boardSortMode === option.value ? "checked" : ""}><span><strong>${option.label}${option.value === "natural" ? " <em>Default</em>" : ""}</strong><small>${option.description}</small></span></label>`).join("")}
    </fieldset>
    <p class="configuration-note">Organization is visual only. The table stays put while you build a move, then sorts again when the next turn begins.</p>
  </section>`;
}

function renderCoach(): string {
  return `<section class="coach-panel">
    <div class="coach-intro"><span>?</span><div><strong>Ask at your level</strong><p>Hints use the exact current game state. Applying one only changes your local workspace; you still decide whether to submit it.</p></div></div>
    <div class="hint-levels">
      <button id="hint-practical" ${disabledTurn()}><span>Practical</span><small>A strong move a human can find</small></button>
      <button id="hint-advanced" ${disabledTurn()}><span>Advanced</span><small>Bounded human-style search; it can miss legal wins</small></button>
      <button id="hint-oracle" ${disabledTurn()}><span>Deep solver</span><small>Unrestricted CP-SAT search; crowded tables may take about a minute</small></button>
    </div>
    ${hintRequestTier ? `<article class="solver-progress" role="status"><span aria-hidden="true"></span><div><strong>${hintRequestTier === "oracle" ? "Deep solver is exploring the full table" : `Calculating ${hintRequestTier} guidance`}</strong><p>${hintRequestTier === "oracle" ? "Dense endgames can take about a minute. You may close this drawer; the search will continue." : "Comparing the bounded candidate moves available at this level."}</p></div></article>` : renderCoachResult()}
    <details class="shortcut-help"><summary>Controls & keyboard shortcuts</summary><dl><div><dt>Tap tiles</dt><dd>Select several at once</dd></div><div><dt>Drag</dt><dd>Move selected tiles together</dd></div><div><dt>⌘/Ctrl Z</dt><dd>Undo</dd></div><div><dt>Shift + ⌘/Ctrl Z</dt><dd>Redo</dd></div><div><dt>Escape</dt><dd>Clear selection</dd></div></dl></details>
  </section>`;
}

function renderCoachResult(): string {
  if (!hint) return `<div class="coach-empty"><strong>No hint requested</strong><p>Try building your own move first. The most useful hint is one you can compare against your decision.</p></div>`;
  if (hint.action?.type === "play") {
    const count = hint.action.rack_tiles?.length || 0;
    return `<article class="hint hint-play"><p class="eyebrow">Coach’s result</p><h3>${hint.search?.winning ? "Winning play found" : "Legal table play found"}</h3><p>${escapeHtml(hint.explanation || "")}</p><p class="hint-action-summary">Uses ${count} tile${count === 1 ? "" : "s"} from your rack.</p><button id="apply-hint" class="primary">${hint.search?.winning ? "Place winning move on my table" : "Place this move on my table"}</button></article>`;
  }
  const drawOnly = hint.action?.type === "draw";
  const explanation = drawOnly
    ? "The solver did not return a table play. Drawing is the available action now."
    : hint.explanation || "No table play was found in this search.";
  return `<article class="hint hint-no-play ${hint.search?.status === "budget_exhausted" ? "search-limited" : ""}"><p class="eyebrow">Coach’s result</p><h3>No table play found</h3><p>${escapeHtml(explanation)}</p>${hint.search?.exhaustive === false || hint.search?.status === "budget_exhausted" ? `<p class="search-caveat"><strong>Search limit:</strong> This does not prove that a playable or winning arrangement is impossible.</p>` : ""}<button id="return-to-draw" class="primary">Return to the table and draw</button></article>`;
}

function renderPlayers(): string {
  if (!game) return "";
  return `<section><div class="player-detail-list">${game.opponents.map((opponent) => `<article><span class="opponent-avatar">P${opponent.seat + 1}</span><div><strong>${displayName(opponent.policy)}</strong><p>${displayName(opponent.mechanical_tier)} mechanics · ${opponent.opened ? "opening complete" : "opening not complete"}</p><small>Current plan: ${displayName(opponent.strategy_mode)}</small></div><b>${opponent.rack_count}<small> tiles</small></b></article>`).join("")}</div>${renderMatchStatus(game.match)}</section>`;
}

function renderMatchStatus(match: MatchView | null): string {
  if (!match) return `<div class="drawer-empty"><strong>Single game</strong><p>There are no cumulative standings in this format.</p></div>`;
  const target = match.profile.type === "family_score" ? match.profile.games : match.profile.rounds * match.profile.players;
  return `<section class="match-status" aria-label="Match standings"><div class="section-heading"><div><p class="eyebrow">Finite match</p><h3>Game ${Math.min(match.completed_games + 1, target)} of ${target}</h3></div><small>${match.profile.type === "family_score" ? "Highest cumulative score wins" : "Most game wins; score breaks ties"}</small></div><div class="standings">${match.cumulative_scores.map((score, seat) => `<div class="${seat === game?.human_seat ? "human-standing" : ""}"><span>${seat === game?.human_seat ? "You" : `Player ${seat + 1}`}</span><strong>${score > 0 ? "+" : ""}${score}</strong><small>${match.game_wins[seat]} win${match.game_wins[seat] === 1 ? "" : "s"}</small></div>`).join("")}</div></section>`;
}

function renderActivity(): string {
  if (!game) return "";
  return `<section>${renderBotMoves(game.bot_moves)}${renderHistory(game.history)}</section>`;
}

function renderBotMoves(moves: BotMove[]): string {
  if (!moves.length) return `<div class="drawer-empty"><strong>No opponent moves yet</strong><p>The last round of opponent decisions will appear here with an explanation.</p></div>`;
  botStep = Math.min(botStep, moves.length - 1);
  const move = moves[botStep];
  const action = String(move.action.type || "move");
  const played = new Set(Array.isArray(move.action.rack_tiles) ? move.action.rack_tiles.map(Number) : []);
  const moved = movedTiles(move.before, move.after);
  const playedLabels = [...played].map((tile) => tileInfo(tile).label).join(", ");
  const summary = action === "play"
    ? `<p class="move-summary"><strong>Played ${played.size} from rack:</strong> ${escapeHtml(playedLabels || "unknown tile")}</p>`
    : `<p class="move-summary"><strong>${action === "draw" ? "Drew one tile." : "Passed."}</strong> The shared table did not change.</p>`;
  const tableChange = action === "play"
    ? `<div class="diff"><div><small>Before</small>${miniTable(move.before, moved)}</div><span>→</span><div><small>After</small>${miniTable(move.after, moved, played)}</div></div><small>${moved.size ? `${moved.size} existing table tile${moved.size === 1 ? "" : "s"} changed meld membership (gold). Newly played tiles are green.` : "No existing table melds changed. Newly played tiles are green."}</small>`
    : "";
  return `<section class="bot-moves"><div class="section-heading"><div><p class="eyebrow">What changed</p><h3>Player ${move.player + 1}’s move</h3></div><div class="step-controls"><button id="bot-previous" ${botStep === 0 ? "disabled" : ""} aria-label="Previous opponent move">‹</button><span>${botStep + 1} / ${moves.length}</span><button id="bot-next" ${botStep === moves.length - 1 ? "disabled" : ""} aria-label="Next opponent move">›</button></div></div>${summary}<p>${escapeHtml(move.explanation)}</p>${tableChange}</section>`;
}

function renderHistory(history: GameView["history"]): string {
  if (!history.length) return "";
  return `<section class="history"><div class="section-heading"><div><p class="eyebrow">Timeline</p><h3>${history.length} actions</h3></div></div><ol>${history.slice(-20).reverse().map((event) => `<li><span class="history-turn">${event.turn_index + 1}</span><p><strong>${event.player === game?.human_seat ? "You" : `Player ${event.player + 1}`}</strong> ${event.type}</p></li>`).join("")}</ol></section>`;
}

function renderReplay(): string {
  if (!replay) return `<div class="drawer-empty"><strong>Replay available when the game ends</strong><p>The server will verify every transition before presenting it here.</p>${game?.outcome ? `<button id="load-replay" class="primary">Load verified replay</button>` : ""}</div>`;
  const snapshot = replay.snapshots[replayStep];
  return `<section class="replay" aria-label="Annotated replay"><div class="replay-verified">${replay.verified ? "✓ Verified reconstruction" : "Unverified"}</div><p class="eyebrow">Step ${replayStep} of ${replay.snapshots.length - 1}</p><h3>${escapeHtml(snapshot.label)}</h3>${miniTable(snapshot.table)}<div class="replay-meta"><span>Pool ${snapshot.pool_size}</span><span>Rack counts ${snapshot.rack_counts.join(" · ")}</span></div><div class="step-controls replay-controls"><button id="replay-start" ${replayStep === 0 ? "disabled" : ""}>Start</button><button id="replay-previous" ${replayStep === 0 ? "disabled" : ""}>Previous</button><button id="replay-next" ${replayStep === replay.snapshots.length - 1 ? "disabled" : ""}>Next</button><button id="replay-end" ${replayStep === replay.snapshots.length - 1 ? "disabled" : ""}>End</button></div></section>`;
}

function renderOutcome(): string {
  if (!game?.outcome) return "";
  const matchComplete = game.match?.complete;
  const humanWon = game.outcome.winners.includes(game.human_seat);
  return `<div class="outcome-backdrop"><section class="outcome" role="dialog" aria-modal="true" aria-labelledby="outcome-title"><button id="dismiss-outcome" class="icon-button outcome-close" aria-label="View final table" title="View final table">×</button><span class="outcome-mark">${humanWon ? "★" : "◆"}</span><p class="eyebrow">${matchComplete ? "Match complete" : "Game complete"}</p><h2 id="outcome-title">${humanWon ? "You won this game" : `${game.outcome.winners.map((seat) => seat === game?.human_seat ? "You" : `Player ${seat + 1}`).join(" & ")} won`}</h2><p class="outcome-scores">${game.outcome.scores.map((score, seat) => `<span><small>${seat === game?.human_seat ? "You" : `P${seat + 1}`}</small><strong>${score > 0 ? "+" : ""}${score}</strong></span>`).join("")}</p>${matchComplete ? `${renderOutcomeMatchScores(game.match)}<p>${game.match?.winners.includes(game.human_seat) ? "You won the match." : `Match winner: ${game.match?.winners.map((seat) => `Player ${seat + 1}`).join(", ")}.`}</p><button id="leave-game" class="primary">Start another match</button>` : game.match ? `<button id="next-game" class="primary">Deal game ${game.match.completed_games + 1}</button>` : `<button id="leave-game" class="primary">Play another game</button>`}<button id="dismiss-outcome-secondary" class="text-button">View final table</button><button id="load-replay" class="text-button">Open annotated verified replay</button><a class="json-link" href="${apiPath(`/api/v1/games/${game.session_id}/replay`)}" target="_blank" rel="noopener">JSON</a></section></div>`;
}

function renderPostGameAction(): string {
  if (!game?.outcome) return "";
  if (game.match && !game.match.complete) {
    return `<button id="next-game" class="primary submit-button post-game-button"><span>Next game</span><small>Deal game ${game.match.completed_games + 1}</small></button>`;
  }
  return `<button id="leave-game" class="primary submit-button post-game-button"><span>${game.match ? "New match" : "Play again"}</span><small>Choose your table</small></button>`;
}

function renderOutcomeMatchScores(match: MatchView | null): string {
  if (!match) return "";
  const target = match.profile.type === "family_score" ? match.profile.games : match.profile.rounds * match.profile.players;
  return `<section class="outcome-match" aria-label="Match standings"><strong>Final standings · Game ${Math.min(match.completed_games, target)} of ${target}</strong><div class="standings">${match.cumulative_scores.map((score, seat) => `<div class="${seat === game?.human_seat ? "human-standing" : ""}"><span>${seat === game?.human_seat ? "You" : `Player ${seat + 1}`}</span><strong>${score > 0 ? "+" : ""}${score}</strong><small>${match.game_wins[seat]} win${match.game_wins[seat] === 1 ? "" : "s"}</small></div>`).join("")}</div></section>`;
}

function renderMeld(meld: Meld, index: number): string {
  const status = clientMeldValid(meld);
  const selectedCount = selections.length;
  const canPlace = selectedCount > 0 && game?.human_turn && !game.outcome && !requestInFlight && !opponentPlayback;
  const stateLabel = meld.length ? status ? "Valid meld" : "Incomplete meld" : "Empty meld";
  const gutterLabel = canPlace
    ? `Add ${selectedCount} selected tile${selectedCount === 1 ? "" : "s"} to meld ${index + 1}`
    : stateLabel;
  return `<div class="meld drop-zone ${meld.length ? status ? "valid" : "invalid" : "empty-meld"}" data-target="meld" data-index="${index}" tabindex="0" aria-label="Meld ${index + 1}">
    ${meld.map((tile) => renderTile(tile, { kind: "meld", index })).join("") || `<small>Select tiles, then use the gutter</small>`}<button type="button" class="meld-gutter ${canPlace ? "ready" : ""}" aria-label="${gutterLabel}" ${canPlace ? "" : "disabled"}><span aria-hidden="true">${canPlace ? "+" : meld.length ? status ? "✓" : "!" : ""}</span></button>
  </div>`;
}

function renderTile(tile: number, source: Source): string {
  const info = tileInfo(tile);
  const selected = selectionIndex({ tile, source }) >= 0;
  const playbackClass = playbackTileClass(tile);
  const justDrawn = source.kind === "rack" && recentDrawnTile === tile;
  return `<button type="button" class="tile ${info.className} ${selected ? "selected" : ""} ${justDrawn ? "just-drawn" : ""} ${justDrawn && recentDrawIndicatorFresh ? "is-fresh" : ""} ${playbackClass}" draggable="${game?.human_turn && !opponentPlayback ? "true" : "false"}" data-tile="${tile}" data-source='${JSON.stringify(source)}' aria-label="${info.label}${justDrawn ? ", just drawn" : ""}" aria-pressed="${selected}"><span class="tile-number">${info.number}</span><span class="tile-color">${info.colorLabel}</span>${justDrawn ? `<span class="drawn-badge" aria-hidden="true">New</span>` : ""}<span class="selection-check" aria-hidden="true">✓</span></button>`;
}

function currentPlaybackMove(): BotMove | null {
  return opponentPlayback?.moves[opponentPlayback.index] || null;
}

function playbackTileClass(tile: number): string {
  if (opponentPlayback?.phase !== "result") return "";
  const move = currentPlaybackMove();
  if (!move) return "";
  const played = new Set(Array.isArray(move.action.rack_tiles) ? move.action.rack_tiles.map(Number) : []);
  if (played.has(tile)) return "opponent-added";
  if (movedTiles(move.before, move.after).has(tile)) return "opponent-moved";
  return "";
}

function miniTable(table: Meld[], moved: Set<number> = new Set(), added: Set<number> = new Set()): string {
  return `<div class="mini-table">${table.map((meld) => `<span class="mini-meld">${meld.map((tile) => `<i class="mini-tile ${added.has(tile) ? "added" : moved.has(tile) ? "moved" : ""}">${tileShort(tile)}</i>`).join("")}</span>`).join("") || "<span>Empty table</span>"}</div>`;
}

function bindEvents(): void {
  on("#new-game", "click", () => void createGame());
  on("#randomize-opponents", "click", randomizeOpponents);
  on("#player-count", "change", updateOpponentVisibility);
  on("#turn-timer", "change", (event) => {
    timerSeconds = Number((event.target as HTMLSelectElement).value);
    localStorage.setItem("rummikub-turn-timer", String(timerSeconds));
  });
  onAll("#leave-game", "click", leaveGame);
  onAll("#next-game", "click", () => void nextGame());
  on("#dismiss-outcome", "click", dismissOutcome);
  on("#dismiss-outcome-secondary", "click", dismissOutcome);
  on("#new-meld", "click", () => makeMeldFromSelections());
  on("#make-meld", "click", makeMeldFromSelections);
  on("#return-selected", "click", returnSelectionsToRack);
  on("#clear-selection", "click", clearSelections);
  on("#sort-rack", "click", toggleRackSort);
  on("#board-view", "click", toggleBoardView);
  on("#next-playback", "click", manuallyAdvanceOpponentPlayback);
  on("#skip-playback", "click", finishOpponentPlayback);
  const playback = document.querySelector<HTMLElement>(".opponent-playback");
  playback?.addEventListener("mouseenter", pauseOpponentPlayback);
  playback?.addEventListener("mouseleave", schedulePlaybackAdvance);
  playback?.addEventListener("focusin", pauseOpponentPlayback);
  playback?.addEventListener("focusout", schedulePlaybackAdvance);
  on("#undo", "click", undo);
  on("#redo", "click", redo);
  on("#reset", "click", resetWorkspace);
  on("#preview", "click", () => void preview(true));
  on("#draw", "click", () => void drawOrPass());
  on("#submit", "click", () => void submitPlay());
  on("#hint-practical", "click", () => void requestHint("practical"));
  on("#hint-advanced", "click", () => void requestHint("advanced"));
  on("#hint-oracle", "click", () => void requestHint("oracle"));
  on("#apply-hint", "click", applyHint);
  on("#return-to-draw", "click", returnToDraw);
  onAll("#load-replay", "click", () => void loadReplay());
  on("#bot-previous", "click", () => { botStep -= 1; render(); });
  on("#bot-next", "click", () => { botStep += 1; render(); });
  on("#replay-start", "click", () => { replayStep = 0; render(); });
  on("#replay-previous", "click", () => { replayStep -= 1; render(); });
  on("#replay-next", "click", () => { replayStep += 1; render(); });
  on("#replay-end", "click", () => { if (replay) replayStep = replay.snapshots.length - 1; render(); });
  on("#close-drawer", "click", closeDrawer);
  on("#close-drawer-scrim", "click", closeDrawer);
  document.querySelectorAll<HTMLElement>("[data-open-drawer]").forEach((element) => element.addEventListener("click", () => {
    const requested = element.dataset.openDrawer as DrawerTab;
    if (drawer === requested) drawer = null;
    else {
      drawerOpening = drawer === null;
      drawer = requested;
    }
    render();
  }));
  document.querySelectorAll<HTMLElement>("[data-drawer-tab]").forEach((element) => element.addEventListener("click", () => {
    drawerOpening = false;
    drawer = element.dataset.drawerTab as DrawerTab;
    render();
  }));
  document.querySelectorAll<HTMLInputElement>('input[name="opponent-speed"]').forEach((input) => input.addEventListener("change", () => {
    if (!input.checked) return;
    setOpponentSpeed(input.value as OpponentSpeed);
  }));
  document.querySelectorAll<HTMLInputElement>('input[name="board-sort"]').forEach((input) => input.addEventListener("change", () => {
    if (!input.checked) return;
    setBoardSortMode(input.value as BoardSortMode);
  }));

  document.querySelectorAll<HTMLElement>(".tile").forEach((element) => {
    element.addEventListener("click", (event) => selectTile(element, event as MouseEvent));
    element.addEventListener("dragstart", (event) => startDrag(event as DragEvent, element));
  });
  document.querySelectorAll<HTMLElement>(".drop-zone").forEach((zone) => {
    zone.addEventListener("dragover", (event) => { event.preventDefault(); zone.classList.add("drag-over"); });
    zone.addEventListener("dragleave", () => zone.classList.remove("drag-over"));
    zone.addEventListener("drop", (event) => dropSelections(event as DragEvent, zone));
    zone.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (selections.length && (target === zone || target.classList.contains("rack-tier") || target.closest(".meld-gutter"))) moveSelections(zone);
    });
    zone.addEventListener("keydown", (event) => { if (selections.length && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); moveSelections(zone); } });
  });
  updateOpponentVisibility();
}

function randomizeOpponents(): void {
  const count = Number(document.querySelector<HTMLSelectElement>("#player-count")?.value || "4");
  const choices = strategyGroups.flatMap((group) => group.values);
  for (let index = choices.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [choices[index], choices[swap]] = [choices[swap], choices[index]];
  }
  for (let seat = 1; seat < count; seat += 1) {
    const select = document.querySelector<HTMLSelectElement>(`#opponent-${seat}`);
    if (select) select.value = choices[seat - 1];
    const tier = document.querySelector<HTMLSelectElement>(`#tier-${seat}`);
    if (tier) tier.value = Math.random() < .5 ? "practical" : "advanced";
  }
  message = "Opponent strategies and difficulty levels randomized.";
  const status = document.querySelector<HTMLElement>("[role=status]");
  if (status) status.textContent = message;
}

function on(selector: string, event: string, listener: EventListener): void { document.querySelector(selector)?.addEventListener(event, listener); }
function onAll(selector: string, event: string, listener: EventListener): void { document.querySelectorAll(selector).forEach((element) => element.addEventListener(event, listener)); }

document.addEventListener("keydown", (event) => {
  if (!game) { if (event.key === "Enter" && document.activeElement?.tagName !== "SELECT") void createGame(); return; }
  const command = event.metaKey || event.ctrlKey;
  if (command && event.key.toLowerCase() === "z") { event.preventDefault(); event.shiftKey ? redo() : undo(); }
  else if (command && event.key.toLowerCase() === "y") { event.preventDefault(); redo(); }
  else if (event.key === "Escape") { if (drawer) closeDrawer(); else if (game.outcome && !outcomeDismissed) dismissOutcome(); else clearSelections(); }
});

function updateOpponentVisibility(): void {
  const count = Number(document.querySelector<HTMLSelectElement>("#player-count")?.value || "4");
  document.querySelectorAll<HTMLElement>("[data-opponent-seat]").forEach((element) => {
    const visible = Number(element.dataset.opponentSeat) < count;
    element.hidden = !visible;
    element.querySelectorAll<HTMLSelectElement>("select").forEach((select) => { select.disabled = !visible; });
  });
}

function randomGameSeed(): number {
  if (globalThis.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0] || 1;
  }
  return Math.floor(Math.random() * 0xffff_ffff) + 1;
}

function newlyAddedTile(before: number[], after: number[]): number | null {
  const previous = new Set(before);
  return after.find((tile) => !previous.has(tile)) ?? null;
}

async function createGame(): Promise<void> {
  const seedValue = document.querySelector<HTMLInputElement>("#seed")?.value.trim() || "";
  const seed = seedValue ? Number(seedValue) : randomGameSeed();
  const player_count = Number(document.querySelector<HTMLSelectElement>("#player-count")?.value || "4");
  const opponent_policies = [1, 2, 3].slice(0, player_count - 1).map((seat) => document.querySelector<HTMLSelectElement>(`#opponent-${seat}`)?.value || "balanced");
  const opponent_tiers = [1, 2, 3].slice(0, player_count - 1).map((seat) => document.querySelector<HTMLSelectElement>(`#tier-${seat}`)?.value || "practical");
  const match_profile = document.querySelector<HTMLSelectElement>("#match-profile")?.value || "single_game";
  requestInFlight = true;
  try {
    const response = await api<GameView>("/api/v1/games", { method: "POST", body: JSON.stringify({ seed, player_count, starting_player: 0, opponent_policies, opponent_tiers, match_profile }) });
    setGame(response, "Tiles dealt. Select several rack tiles, then make your first meld.");
  } catch (error) { setMessage(error); }
  finally { requestInFlight = false; }
}

async function nextGame(): Promise<void> {
  if (!game) return;
  try {
    const response = await api<GameView>(`/api/v1/games/${game.session_id}/next-game`, { method: "POST" });
    setGame(response, `Game ${(response.match?.completed_games ?? 0) + 1} is ready.`, true);
  } catch (error) { setMessage(error); }
}

function leaveGame(): void {
  if (game && !game.outcome && !window.confirm("Leave this game? Your server session will remain available, but this browser will forget it.")) return;
  game = null;
  if (timerHandle !== null) window.clearInterval(timerHandle);
  timerHandle = null;
  clearOpponentPlayback();
  recentDrawnTile = null;
  outcomeDismissed = false;
  localStorage.removeItem("rummikub-session");
  message = "Choose your opponents and deal a game.";
  render();
}

function dismissOutcome(): void {
  if (!game?.outcome) return;
  outcomeDismissed = true;
  message = game.match && !game.match.complete
    ? "Final table shown. Deal the next game when you are ready."
    : "Final table shown. Use Play again when you are ready.";
  render();
}

async function preview(manual = false): Promise<void> {
  if (!game || !workspaceDirty()) { validation = { state: "idle", message: "Move tiles to begin." }; if (manual) render(); return; }
  const signature = workspaceSignature();
  validation = { state: "checking", message: "Checking the full table…" };
  render();
  try {
    const response = await api<{ valid: boolean; message: string }>(`/api/v1/games/${game.session_id}/validate`, { method: "POST", body: JSON.stringify({ revision: game.revision, proposed_table: cleanTable(), rack_tiles: playedRackTiles() }) });
    if (signature !== workspaceSignature()) return;
    validation = { state: response.valid ? "legal" : "illegal", message: friendlyValidation(response.message) };
    if (manual) message = friendlyValidation(response.message);
  } catch (error) {
    if (signature !== workspaceSignature()) return;
    validation = { state: "illegal", message: errorMessage(error) };
    if (manual) message = errorMessage(error);
  }
  render();
}

function schedulePreview(): void {
  if (validationHandle !== null) window.clearTimeout(validationHandle);
  validation = workspaceDirty() ? { state: "checking", message: "Checking the full table…" } : { state: "idle", message: "Move tiles to begin." };
  validationHandle = window.setTimeout(() => { validationHandle = null; void preview(); }, 450);
}

async function submitPlay(): Promise<void> {
  if (!game) return;
  if (!playedRackTiles().length) { message = "A move must add at least one tile from your rack."; render(); return; }
  await submitAction({ type: "play", proposed_table: cleanTable(), rack_tiles: playedRackTiles() });
}

async function drawOrPass(): Promise<void> {
  if (!game) return;
  if (workspaceDirty() && !window.confirm(`Discard your table changes and ${game.pool_size ? "draw a tile" : "pass"}?`)) return;
  await submitAction({ type: game.pool_size ? "draw" : "pass" });
}

async function submitAction(action: Record<string, unknown>): Promise<void> {
  if (!game || requestInFlight) return;
  const sessionId = game.session_id;
  const submittedRevision = game.revision;
  const rackBefore = [...game.rack];
  requestInFlight = true;
  message = action.type === "play" ? "Submitting your move…" : action.type === "draw" ? "Drawing a tile…" : "Passing…";
  render();
  try {
    const response = await api<GameView>(`/api/v1/games/${sessionId}/actions`, { method: "POST", body: JSON.stringify({ revision: submittedRevision, ...action }) });
    const drawnTile = action.type === "draw" ? newlyAddedTile(rackBefore, response.rack) : null;
    setGame(response, response.outcome ? "Game complete." : "Move accepted. Opponents played; it’s your turn again.", true, drawnTile);
  } catch (error) {
    if (error instanceof TypeError && await recoverSubmittedSession(sessionId, submittedRevision, action.type === "draw" ? rackBefore : null)) return;
    requestInFlight = false;
    setMessage(error);
  }
}

async function recoverSubmittedSession(sessionId: string, submittedRevision: number, rackBefore: number[] | null): Promise<boolean> {
  try {
    const current = await api<GameView>(`/api/v1/games/${sessionId}`);
    if (current.revision <= submittedRevision) return false;
    requestInFlight = false;
    setGame(current, current.outcome ? "Game complete. Your move was accepted despite an interrupted response." : "Your move was accepted. The interrupted response was recovered safely.", true, rackBefore ? newlyAddedTile(rackBefore, current.rack) : null);
    return true;
  } catch { return false; }
}

async function requestHint(tier: "practical" | "advanced" | "oracle"): Promise<void> {
  if (!game) return;
  message = tier === "oracle" ? "Running the deep solver…" : `Calculating a ${tier} hint…`;
  hintRequestTier = tier;
  requestInFlight = true;
  render();
  try {
    hint = await api(`/api/v1/games/${game.session_id}/hint`, { method: "POST", body: JSON.stringify({ tier }) });
    message = "Hint ready. Compare it with the move you were considering.";
  } catch (error) { setMessage(error); }
  finally { hintRequestTier = null; requestInFlight = false; render(); }
}

async function loadReplay(): Promise<void> {
  if (!game) return;
  try {
    replay = await api<ReplayView>(`/api/v1/games/${game.session_id}/replay`);
    replayStep = 0;
    drawer = "replay";
    message = "Verified replay loaded.";
    render();
  } catch (error) { setMessage(error); }
}

function applyHint(): void {
  if (!hint?.action || hint.action.type !== "play") return;
  pushUndo();
  workingTable = (hint.action.proposed_table || []).map(orderMeld);
  const played = new Set(hint.action.rack_tiles || []);
  workingRack = sortRack(game?.rack.filter((tile) => !played.has(tile)) || []);
  selections = [];
  drawer = null;
  message = "Suggested layout placed on the table. Review it, then submit when ready.";
  schedulePreview();
  render();
}

function returnToDraw(): void {
  drawer = null;
  message = "No table play was found. Draw when you are ready.";
  render();
  document.querySelector<HTMLButtonElement>("#draw")?.focus();
}

function setGame(next: GameView, nextMessage: string, playOpponentMoves = false, drawnTile: number | null = null): void {
  clearOpponentPlayback();
  game = next;
  workingRack = sortRack(next.rack);
  workingTable = organizeTable(next.table);
  undoStack = [];
  redoStack = [];
  selections = [];
  hint = null;
  botStep = 0;
  replay = null;
  replayStep = 0;
  drawer = null;
  drawerOpening = false;
  recentDrawnTile = drawnTile;
  recentDrawIndicatorFresh = drawnTile !== null;
  outcomeDismissed = false;
  validation = { state: "idle", message: "Move tiles to begin." };
  requestInFlight = false;
  message = nextMessage;
  localStorage.setItem("rummikub-session", next.session_id);
  if (playOpponentMoves && next.bot_moves.length) startOpponentPlayback(next.bot_moves, nextMessage);
  else { render(); startTurnTimer(); }
}

function startOpponentPlayback(moves: BotMove[], finalMessage: string): void {
  if (opponentSpeed === "instant") {
    opponentPlayback = null;
    if (game) workingTable = organizeTable(game.table);
    message = finalMessage;
    render();
    startTurnTimer();
    return;
  }
  opponentPlayback = { moves, index: 0, phase: "thinking", finalMessage, manual: false };
  workingTable = organizeTable(moves[0].before);
  message = `Player ${moves[0].player + 1} is deciding…`;
  timerDeadline = 0;
  if (timerHandle !== null) window.clearInterval(timerHandle);
  timerHandle = null;
  render();
  schedulePlaybackAdvance();
}

function schedulePlaybackAdvance(): void {
  if (!opponentPlayback || opponentPlayback.manual) return;
  if (playbackHandle !== null) window.clearTimeout(playbackHandle);
  const timings: Record<Exclude<OpponentSpeed, "instant">, { thinking: number; result: number }> = {
    quick: { thinking: 550, result: 1000 },
    natural: { thinking: 1200, result: 2400 },
    thoughtful: { thinking: 2000, result: 4000 },
  };
  const selected = opponentSpeed === "instant" ? timings.quick : timings[opponentSpeed];
  const delay = selected[opponentPlayback.phase];
  playbackHandle = window.setTimeout(advanceOpponentPlayback, delay);
}

function readOpponentSpeed(): OpponentSpeed {
  const stored = localStorage.getItem("rummikub-opponent-speed");
  return opponentSpeedOptions.some((option) => option.value === stored) ? stored as OpponentSpeed : "natural";
}

function setOpponentSpeed(speed: OpponentSpeed): void {
  if (!opponentSpeedOptions.some((option) => option.value === speed)) return;
  opponentSpeed = speed;
  localStorage.setItem("rummikub-opponent-speed", speed);
  message = `Opponent playback set to ${opponentSpeedOptions.find((option) => option.value === speed)?.label}.`;
  if (speed === "instant" && opponentPlayback) {
    finishOpponentPlayback();
    return;
  }
  if (opponentPlayback) {
    opponentPlayback.manual = false;
    pauseOpponentPlayback();
  }
  render();
  if (opponentPlayback) schedulePlaybackAdvance();
}

function readBoardSortMode(): BoardSortMode {
  const stored = localStorage.getItem("rummikub-board-sort");
  return boardSortOptions.some((option) => option.value === stored) ? stored as BoardSortMode : "natural";
}

function setBoardSortMode(mode: BoardSortMode): void {
  if (!boardSortOptions.some((option) => option.value === mode)) return;
  boardSortMode = mode;
  localStorage.setItem("rummikub-board-sort", mode);
  if (mode === "natural" && game && !workspaceDirty() && !opponentPlayback) {
    replaceWorkingTablePreservingSelections(game.table.map(orderMeld));
  } else {
    organizeWorkingTable();
  }
  message = mode === "natural"
    ? "Board organization set to Natural table. New turns will keep the order played."
    : `Board organization set to ${boardSortOptions.find((option) => option.value === mode)?.label}.`;
  render();
}

function pauseOpponentPlayback(): void {
  if (playbackHandle !== null) window.clearTimeout(playbackHandle);
  playbackHandle = null;
}

function manuallyAdvanceOpponentPlayback(): void {
  if (!opponentPlayback) return;
  opponentPlayback.manual = true;
  pauseOpponentPlayback();
  advanceOpponentPlayback();
}

function advanceOpponentPlayback(): void {
  playbackHandle = null;
  if (!opponentPlayback) return;
  const move = currentPlaybackMove();
  if (!move) { finishOpponentPlayback(); return; }
  if (opponentPlayback.phase === "thinking") {
    opponentPlayback.phase = "result";
    workingTable = organizeTable(move.after);
    message = playbackMoveSummary(move);
    render();
    schedulePlaybackAdvance();
    return;
  }
  opponentPlayback.index += 1;
  if (opponentPlayback.index >= opponentPlayback.moves.length) { finishOpponentPlayback(); return; }
  opponentPlayback.phase = "thinking";
  const next = currentPlaybackMove()!;
  workingTable = organizeTable(next.before);
  message = `Player ${next.player + 1} is deciding…`;
  render();
  schedulePlaybackAdvance();
}

function playbackMoveSummary(move: BotMove): string {
  const action = String(move.action.type || "move");
  const count = Array.isArray(move.action.rack_tiles) ? move.action.rack_tiles.length : 0;
  if (action === "play") return `Player ${move.player + 1} played ${count} tile${count === 1 ? "" : "s"}.`;
  if (action === "draw") return `Player ${move.player + 1} drew a tile.`;
  return `Player ${move.player + 1} passed.`;
}

function finishOpponentPlayback(): void {
  const finalMessage = opponentPlayback?.finalMessage;
  clearOpponentPlayback();
  if (game) workingTable = organizeTable(game.table);
  if (finalMessage) message = finalMessage;
  render();
  startTurnTimer();
}

function clearOpponentPlayback(): void {
  if (playbackHandle !== null) window.clearTimeout(playbackHandle);
  playbackHandle = null;
  opponentPlayback = null;
}

function startTurnTimer(): void {
  if (timerHandle !== null) window.clearInterval(timerHandle);
  timerHandle = null;
  timerDeadline = game?.human_turn && timerSeconds ? Date.now() + timerSeconds * 1000 : 0;
  updateTimerDisplay();
  if (timerDeadline) timerHandle = window.setInterval(updateTimerDisplay, 250);
}

function updateTimerDisplay(): void {
  const display = document.querySelector<HTMLElement>("#turn-timer-display");
  if (!display) return;
  if (!timerDeadline) { display.textContent = "Off"; return; }
  const remaining = Math.max(0, Math.ceil((timerDeadline - Date.now()) / 1000));
  display.textContent = remaining ? `${remaining}s` : "Time";
  display.classList.toggle("expired", remaining === 0);
  if (!remaining && timerHandle !== null) { window.clearInterval(timerHandle); timerHandle = null; }
}

function currentSnapshot(): Snapshot { return { rack: [...workingRack], table: workingTable.map((meld) => [...meld]) }; }
function applySnapshot(snapshot: Snapshot): void { workingRack = [...snapshot.rack]; workingTable = snapshot.table.map((meld) => [...meld]); selections = []; schedulePreview(); render(); }
function pushUndo(): void { undoStack.push(currentSnapshot()); redoStack = []; }
function undo(): void { const snapshot = undoStack.pop(); if (!snapshot) return; redoStack.push(currentSnapshot()); message = "Undid the last table change."; applySnapshot(snapshot); }
function redo(): void { const snapshot = redoStack.pop(); if (!snapshot) return; undoStack.push(currentSnapshot()); message = "Restored the table change."; applySnapshot(snapshot); }

function resetWorkspace(): void {
  if (!game) return;
  if (workspaceDirty()) undoStack.push(currentSnapshot());
  workingRack = sortRack(game.rack);
  workingTable = organizeTable(game.table);
  redoStack = [];
  selections = [];
  validation = { state: "idle", message: "Move tiles to begin." };
  message = "Turn reset to the last submitted table.";
  render();
}

function mutate(change: () => void, nextMessage = "Table updated."): void {
  if (!game?.human_turn || opponentPlayback) return;
  pushUndo();
  change();
  removeEmptyMelds();
  workingTable = workingTable.map(orderMeld);
  selections = [];
  message = nextMessage;
  schedulePreview();
  render();
}

function selectTile(element: HTMLElement, event: MouseEvent): void {
  if (!game?.human_turn) return;
  const candidate = selectionFrom(element);
  if (opponentPlayback && candidate.source.kind !== "rack") return;
  const index = selectionIndex(candidate);
  if (event.shiftKey && candidate.source.kind === "rack" && selections.length) {
    const anchor = selections[selections.length - 1];
    if (anchor.source.kind === "rack") {
      const start = workingRack.indexOf(anchor.tile);
      const end = workingRack.indexOf(candidate.tile);
      const range = workingRack.slice(Math.min(start, end), Math.max(start, end) + 1).map((tile) => ({ tile, source: { kind: "rack" } as Source }));
      selections = uniqueSelections([...selections, ...range]);
    }
  } else if (index >= 0) selections.splice(index, 1);
  else selections.push(candidate);
  message = selections.length ? `${selections.length} tile${selections.length === 1 ? "" : "s"} selected. Tap a + on a meld or make a new one.` : "Selection cleared.";
  render();
}

function startDrag(event: DragEvent, element: HTMLElement): void {
  if (opponentPlayback) { event.preventDefault(); return; }
  const candidate = selectionFrom(element);
  if (selectionIndex(candidate) < 0) selections = [candidate];
  event.dataTransfer?.setData("application/json", JSON.stringify(selections));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
}

function dropSelections(event: DragEvent, zone: HTMLElement): void {
  event.preventDefault();
  zone.classList.remove("drag-over");
  const payload = event.dataTransfer?.getData("application/json");
  if (payload) selections = JSON.parse(payload) as Selection[];
  moveSelections(zone);
}

function moveSelections(target: HTMLElement): void {
  if (!game?.human_turn || opponentPlayback || !selections.length) return;
  const moving = uniqueSelections(selections);
  const targetKind = target.dataset.target;
  if (targetKind === "rack" && moving.some((selection) => !game?.rack.includes(selection.tile))) {
    message = "Only tiles that came from your rack can return there.";
    render();
    return;
  }
  mutate(() => {
    moving.forEach(removeTile);
    if (targetKind === "rack") workingRack.push(...moving.map((selection) => selection.tile));
    else if (targetKind === "new-meld") workingTable.push(moving.map((selection) => selection.tile));
    else workingTable[Number(target.dataset.index)].push(...moving.map((selection) => selection.tile));
  }, `${moving.length} tile${moving.length === 1 ? "" : "s"} moved.`);
}

function makeMeldFromSelections(): void {
  if (!selections.length) { mutate(() => workingTable.push([]), "Empty meld space added. Select tiles, then tap it."); return; }
  const moving = uniqueSelections(selections);
  mutate(() => { moving.forEach(removeTile); workingTable.push(moving.map((selection) => selection.tile)); }, `New meld made with ${moving.length} tile${moving.length === 1 ? "" : "s"}.`);
}

function returnSelectionsToRack(): void {
  if (!game || !canReturnSelection()) return;
  const moving = uniqueSelections(selections);
  mutate(() => { moving.forEach(removeTile); workingRack.push(...moving.map((selection) => selection.tile)); workingRack = sortRack(workingRack); }, `${moving.length} tile${moving.length === 1 ? "" : "s"} returned to your rack.`);
}

function removeTile(selection: Selection): void {
  const remove = (items: number[]) => { const index = items.indexOf(selection.tile); if (index >= 0) items.splice(index, 1); };
  if (selection.source.kind === "rack") remove(workingRack);
  else remove(workingTable[selection.source.index] || []);
}

function clearSelections(): void { if (!selections.length) return; selections = []; message = "Selection cleared."; render(); }
function closeDrawer(): void { drawer = null; drawerOpening = false; render(); }
function selectionFrom(element: HTMLElement): Selection { return { tile: Number(element.dataset.tile), source: JSON.parse(element.dataset.source || "{}") as Source }; }
function selectionIndex(candidate: Selection): number { return selections.findIndex((selection) => selection.tile === candidate.tile && sourceKey(selection.source) === sourceKey(candidate.source)); }
function sourceKey(source: Source): string { return source.kind === "meld" ? `meld-${source.index}` : source.kind; }
function uniqueSelections(items: Selection[]): Selection[] { return items.filter((item, index) => items.findIndex((other) => other.tile === item.tile && sourceKey(other.source) === sourceKey(item.source)) === index); }
function canReturnSelection(): boolean { return Boolean(game && selections.every((selection) => game?.rack.includes(selection.tile))); }
function removeEmptyMelds(): void { workingTable = workingTable.filter((meld) => meld.length); }

function toggleRackSort(): void {
  rackSortMode = rackSortMode === "color" ? "number" : "color";
  localStorage.setItem("rummikub-rack-sort", rackSortMode);
  workingRack = sortRack(workingRack);
  message = `Rack sorted by ${rackSortMode}.`;
  render();
}

function toggleBoardView(): void {
  boardViewMode = boardViewMode === "fit" ? "large" : "fit";
  message = boardViewMode === "fit" ? "The complete table will be fitted into view." : "Large table tiles enabled; crowded boards may scroll.";
  render();
}

function fitTableToViewport(): void {
  const table = document.querySelector<HTMLElement>(".table-surface");
  if (!table) return;
  const levels = ["comfortable", "compact", "dense", "overview"] as const;
  table.classList.remove(...levels.map((level) => `density-${level}`));
  table.style.removeProperty("--tile-width");
  delete table.dataset.tileWidth;
  if (boardViewMode === "large") {
    table.classList.add("density-comfortable");
    table.dataset.density = "comfortable";
    return;
  }
  const probeTile = table.querySelector<HTMLElement>(".tile");
  if (!probeTile) {
    table.classList.add("density-comfortable");
    table.dataset.density = "comfortable";
    return;
  }

  const fitsViewport = (): boolean => {
    void table.offsetHeight;
    return table.scrollHeight <= table.clientHeight + 1 && table.scrollWidth <= table.clientWidth + 1;
  };
  let previousWidth: number | null = null;

  // Start with the most readable style, then interpolate between density
  // presets. Flex wrapping supplies another row when that permits larger
  // tiles; the binary search finds the largest width that still fits.
  for (let index = 0; index < levels.length; index += 1) {
    const level = levels[index];
    table.style.removeProperty("--tile-width");
    table.classList.add(`density-${level}`);
    table.dataset.density = level;
    void table.offsetHeight;
    const levelWidth = probeTile.getBoundingClientRect().width;
    const fits = fitsViewport();
    if (fits) {
      if (previousWidth !== null && previousWidth > levelWidth + .25) {
        let lower = levelWidth;
        let upper = previousWidth;
        for (let step = 0; step < 9; step += 1) {
          const candidate = (lower + upper) / 2;
          table.style.setProperty("--tile-width", `${candidate}px`);
          if (fitsViewport()) lower = candidate;
          else upper = candidate;
        }
        // Leave a small rounding margin so zoom and fractional device pixels
        // cannot turn a fitted table into a one-pixel scrollbar.
        const fittedWidth = Math.max(levelWidth, Math.floor((lower - .2) * 4) / 4);
        table.style.setProperty("--tile-width", `${fittedWidth}px`);
        fitsViewport();
        table.dataset.tileWidth = fittedWidth.toFixed(2);
      } else {
        table.dataset.tileWidth = levelWidth.toFixed(2);
      }
      break;
    }
    previousWidth = levelWidth;
    if (index === levels.length - 1) {
      table.dataset.tileWidth = levelWidth.toFixed(2);
      break;
    }
    table.classList.remove(`density-${level}`);
  }
}

function fitRackToViewport(): void {
  const rack = document.querySelector<HTMLElement>(".rack-tiles");
  if (!rack) return;
  const tiles = [...rack.querySelectorAll<HTMLElement>(".tile")];
  if (!tiles.length) return;
  const tiers = [...rack.querySelectorAll<HTMLElement>(".rack-tier")];
  const setWidth = (width: number): void => {
    rack.style.setProperty("--rack-tile-width", `${width}px`);
    void rack.offsetHeight;
  };
  const fits = (): boolean => {
    const rackBox = rack.getBoundingClientRect();
    const tiersFit = tiers.every((tier) => {
      const tierBox = tier.getBoundingClientRect();
      const tierTiles = [...tier.querySelectorAll<HTMLElement>(".tile")];
      return tierTiles.every((tile) => {
        const box = tile.getBoundingClientRect();
        return box.left >= tierBox.left - .5 && box.right <= tierBox.right + .5;
      });
    });
    const tilesFitVertically = tiles.every((tile) => {
      const box = tile.getBoundingClientRect();
      return box.top >= rackBox.top - .5 && box.bottom <= rackBox.bottom + .5;
    });
    return tiersFit && tilesFitVertically && rack.scrollHeight <= rack.clientHeight + 1;
  };

  const minimum = 24;
  const maximum = 64;
  let fitted = maximum;
  setWidth(maximum);
  if (!fits()) {
    let lower = minimum;
    let upper = maximum;
    setWidth(minimum);
    if (fits()) {
      for (let step = 0; step < 10; step += 1) {
        const candidate = (lower + upper) / 2;
        setWidth(candidate);
        if (fits()) lower = candidate;
        else upper = candidate;
      }
      fitted = Math.floor((lower - .2) * 4) / 4;
    } else {
      fitted = minimum;
    }
    setWidth(fitted);
  }
  rack.dataset.tileWidth = fitted.toFixed(2);
  rack.dataset.density = fitted >= 56 ? "comfortable" : fitted >= 44 ? "compact" : fitted >= 34 ? "dense" : "overview";
}

function splitRackRows(tiles: number[]): [number[], number[]] {
  const sorted = sortRack(tiles);
  if (sorted.length < 2) return [sorted, []];
  const midpoint = sorted.length / 2;
  let bestBoundary = 1;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let boundary = 1; boundary < sorted.length; boundary += 1) {
    const left = tileInfo(sorted[boundary - 1]);
    const right = tileInfo(sorted[boundary]);
    let score = Math.abs(boundary - midpoint) * 100;
    if (rackSortMode === "number") {
      if (left.value === right.value) score += 10_000;
    } else if (left.color === right.color) {
      score += 500;
      if (left.value === right.value || Math.abs(left.value - right.value) === 1) score += 10_000;
    } else {
      score -= 1_000;
    }
    if (score < bestScore) {
      bestScore = score;
      bestBoundary = boundary;
    }
  }
  return [sorted.slice(0, bestBoundary), sorted.slice(bestBoundary)];
}

function sortRack(tiles: number[]): number[] {
  return [...tiles].sort(rackSortMode === "color" ? tileSort : numberSort);
}

function orderMeld(meld: Meld): Meld {
  const tiles = [...meld];
  const numbers = tiles.filter((tile) => tile < 104);
  const jokers = tiles.filter((tile) => tile >= 104).sort((a, b) => a - b);
  const infos = numbers.map((tile) => ({ tile, ...tileInfo(tile) }));
  const values = new Set(infos.map((info) => info.value));
  const colors = new Set(infos.map((info) => info.color));

  if (infos.length && values.size === 1 && colors.size === infos.length && tiles.length <= 4) {
    const availableColors = [0, 1, 2, 3].filter((color) => !colors.has(color));
    const assignedJokers = jokers.map((tile, index) => ({ tile, color: availableColors[index] ?? 4 }));
    return [...infos.map(({ tile, color }) => ({ tile, color })), ...assignedJokers]
      .sort((a, b) => a.color - b.color || a.tile - b.tile)
      .map(({ tile }) => tile);
  }

  if (infos.length && colors.size === 1 && values.size === infos.length && tiles.length <= 13) {
    const lowest = Math.min(...infos.map((info) => info.value));
    const highest = Math.max(...infos.map((info) => info.value));
    const earliestStart = Math.max(1, highest - tiles.length + 1);
    const latestStart = Math.min(lowest, 14 - tiles.length);
    if (earliestStart <= latestStart) {
      const byValue = new Map(infos.map((info) => [info.value, info.tile]));
      const unplacedJokers = [...jokers];
      return Array.from({ length: tiles.length }, (_, index) => {
        const value = latestStart + index;
        return byValue.get(value) ?? unplacedJokers.shift()!;
      });
    }
  }

  return tiles.sort(tileSort);
}

interface MeldSortProfile {
  kind: "run" | "group" | "other";
  number: number;
  color: number;
  signature: string;
}

function meldSortProfile(meld: Meld): MeldSortProfile {
  const ordered = orderMeld(meld);
  const infos = ordered.filter((tile) => tile < 104).map(tileInfo);
  const values = [...new Set(infos.map((info) => info.value))].sort((a, b) => a - b);
  const colors = [...new Set(infos.map((info) => info.color))].sort((a, b) => a - b);
  const signature = [...meld].sort((a, b) => a - b).join(",");
  const isGroup = meld.length >= 3 && meld.length <= 4 && infos.length > 0
    && values.length === 1 && colors.length === infos.length;
  if (isGroup) return { kind: "group", number: values[0], color: colors[0] ?? 4, signature };

  const jokers = meld.length - infos.length;
  const isRun = meld.length >= 3 && meld.length <= 13 && infos.length > 0 && colors.length === 1
    && values.length === infos.length && values[values.length - 1] - values[0] + 1 - values.length <= jokers;
  if (isRun) {
    const earliestStart = Math.max(1, values[values.length - 1] - meld.length + 1);
    const start = Math.min(values[0], 14 - meld.length);
    return { kind: "run", number: Math.max(earliestStart, start), color: colors[0], signature };
  }

  return { kind: "other", number: values[0] ?? 99, color: colors[0] ?? 4, signature };
}

function organizeTable(table: Meld[]): Meld[] {
  const ordered = table.map(orderMeld);
  if (boardSortMode === "natural") return ordered;
  return ordered
    .map((meld, index) => ({ meld, index, profile: meldSortProfile(meld) }))
    .sort((left, right) => compareMeldSortProfiles(left.profile, right.profile) || left.index - right.index)
    .map(({ meld }) => meld);
}

function compareMeldSortProfiles(left: MeldSortProfile, right: MeldSortProfile): number {
  const kindRank = (kind: MeldSortProfile["kind"]): number => {
    if (kind === "other") return 2;
    if (boardSortMode === "runs-first") return kind === "run" ? 0 : 1;
    if (boardSortMode === "groups-first") return kind === "group" ? 0 : 1;
    return kind === "group" ? 0 : 1;
  };
  if (boardSortMode === "number") {
    return left.number - right.number || kindRank(left.kind) - kindRank(right.kind)
      || left.color - right.color || left.signature.localeCompare(right.signature);
  }
  return kindRank(left.kind) - kindRank(right.kind)
    || (left.kind === "run" && right.kind === "run" ? left.color - right.color || left.number - right.number : left.number - right.number || left.color - right.color)
    || left.signature.localeCompare(right.signature);
}

function organizeWorkingTable(): void {
  replaceWorkingTablePreservingSelections(organizeTable(workingTable));
}

function replaceWorkingTablePreservingSelections(table: Meld[]): void {
  const selectedTableTiles = new Set(selections.filter((selection) => selection.source.kind === "meld").map((selection) => selection.tile));
  workingTable = table;
  selections = selections.map((selection) => {
    if (selection.source.kind === "rack" || !selectedTableTiles.has(selection.tile)) return selection;
    const index = workingTable.findIndex((meld) => meld.includes(selection.tile));
    return index < 0 ? selection : { tile: selection.tile, source: { kind: "meld", index } };
  });
}

function cleanTable(): Meld[] { return workingTable.filter((meld) => meld.length).map((meld) => [...meld].sort((a, b) => a - b)); }
function playedRackTiles(): number[] { const remaining = new Set(workingRack); return (game?.rack || []).filter((tile) => !remaining.has(tile)).sort((a, b) => a - b); }
function workspaceDirty(): boolean { return Boolean(game && workspaceSignature() !== authoritativeSignature()); }
function workspaceSignature(): string { return JSON.stringify({ rack: [...workingRack].sort((a, b) => a - b), table: cleanTable().map((meld) => [...meld].sort((a, b) => a - b)).sort(compareMelds) }); }
function authoritativeSignature(): string { return JSON.stringify({ rack: [...(game?.rack || [])].sort((a, b) => a - b), table: (game?.table || []).map((meld) => [...meld].sort((a, b) => a - b)).sort(compareMelds) }); }
function compareMelds(a: Meld, b: Meld): number { return (a[0] ?? -1) - (b[0] ?? -1) || a.length - b.length || a.join(",").localeCompare(b.join(",")); }

function openingSummary(): string {
  const tiles = playedRackTiles();
  if (!tiles.length) return "Select a set or run from your rack";
  const nonJokerPoints = tiles.filter((tile) => tile < 104).reduce((sum, tile) => sum + tileInfo(tile).value, 0);
  return tiles.some((tile) => tile >= 104) ? `${nonJokerPoints}+ points selected (joker value checked on submit)` : `${nonJokerPoints} / 30 face-value points selected`;
}

function validationTitle(): string {
  if (validation.state === "checking") return "Checking move";
  if (validation.state === "legal") return "Ready to submit";
  if (validation.state === "illegal") return "Move needs work";
  return "No changes yet";
}

function friendlyValidation(value: string): string {
  return value.replace(/^legal:?\s*/i, "Every meld is legal. ").replace(/^invalid:?\s*/i, "");
}

function messageTone(): string { return /error|illegal|must|cannot|needs work|failed/i.test(message) ? "error" : /accepted|ready|complete|legal|recovered/i.test(message) ? "success" : "info"; }
function messageIcon(): string { return messageTone() === "error" ? "!" : messageTone() === "success" ? "✓" : "i"; }
function disabledTurn(): string { return game?.human_turn && !game.outcome && !requestInFlight && !opponentPlayback ? "" : "disabled"; }

function clientMeldValid(meld: Meld): boolean {
  if (meld.length < 3) return false;
  const infos = meld.filter((tile) => tile < 104).map(tileInfo);
  if (!infos.length) return meld.length <= 13;
  const isGroup = new Set(infos.map((info) => info.value)).size === 1 && new Set(infos.map((info) => info.colorLabel)).size === infos.length && meld.length <= 4;
  if (isGroup) return true;
  const byColor = new Set(infos.map((info) => info.colorLabel)).size === 1;
  if (!byColor || meld.length > 13) return false;
  const values = infos.map((info) => info.value).sort((a, b) => a - b);
  if (new Set(values).size !== values.length) return false;
  const jokers = meld.length - infos.length;
  return values[values.length - 1] - values[0] + 1 - values.length <= jokers;
}

function movedTiles(before: Meld[], after: Meld[]): Set<number> {
  const memberships = (table: Meld[]) => new Map(table.flatMap((meld) => {
    const signature = [...meld].sort((a, b) => a - b).join(",");
    return meld.map((tile) => [tile, signature] as const);
  }));
  const oldMemberships = memberships(before);
  const newMemberships = memberships(after);
  return new Set([...oldMemberships]
    .filter(([tile, membership]) => newMemberships.has(tile) && newMemberships.get(tile) !== membership)
    .map(([tile]) => tile));
}

function tileSort(a: number, b: number): number { const ai = tileInfo(a); const bi = tileInfo(b); return ai.sort - bi.sort || a - b; }
function numberSort(a: number, b: number): number { const ai = tileInfo(a); const bi = tileInfo(b); return ai.value - bi.value || ai.color - bi.color || a - b; }
function tileShort(tile: number): string { const info = tileInfo(tile); return info.number === "★" ? "J" : `${info.colorLabel[0]}${info.number}`; }
function tileInfo(tile: number): { number: string; value: number; color: number; colorLabel: string; className: string; label: string; sort: number } {
  if (tile >= 104) return { number: "★", value: 30, color: 4, colorLabel: "Joker", className: "joker", label: "Joker", sort: 999 };
  const within = tile % 52;
  const color = Math.floor(within / 13);
  const value = within % 13 + 1;
  const names = ["Red", "Blue", "Black", "Orange"];
  const classes = ["red", "blue", "black", "orange"];
  return { number: String(value), value, color, colorLabel: names[color], className: classes[color], label: `${names[color]} ${value}`, sort: color * 20 + value };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiPath(path), { headers: { "Content-Type": "application/json", ...(init?.headers || {}) }, ...init });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail));
  }
  return response.json() as Promise<T>;
}

function apiPath(path: string): string { return `${window.location.pathname.startsWith("/games/rummikub/play") ? "/games/rummikub/play" : ""}${path}`; }
function sitePath(path: string): string { return window.location.pathname.startsWith("/games/rummikub/play") ? path : "#"; }
function setMessage(error: unknown): void { requestInFlight = false; message = errorMessage(error); render(); }
function errorMessage(error: unknown): string { return error instanceof Error ? error.message : String(error); }
function displayName(value: string): string { return value.split(/[_-]/).map((part) => part[0].toUpperCase() + part.slice(1)).join(" "); }
function escapeHtml(value: string): string { const node = document.createElement("div"); node.textContent = value; return node.innerHTML; }

async function reconnect(): Promise<void> {
  const session = localStorage.getItem("rummikub-session");
  if (!session) return;
  try { setGame(await api<GameView>(`/api/v1/games/${session}`), "Reconnected to your game."); }
  catch { localStorage.removeItem("rummikub-session"); }
}

render();
void reconnect();

window.addEventListener("resize", () => {
  if (!game) return;
  if (resizeHandle !== null) window.clearTimeout(resizeHandle);
  resizeHandle = window.setTimeout(() => {
    resizeHandle = null;
    fitTableToViewport();
    fitRackToViewport();
  }, 80);
});
